<!-- eslint-disable prettier/prettier -->
<template>
  <div class="influence-matrix__container">
    <div class="influence-matrix__grid">
      <svg class="influence-matrix" :class="[`-${matrixType}`]" viewBox="0 0 100 100">
        <g
          class="influence-matrix__area influence-matrix__area_critical"
          transform="translate(51, 1)"
        >
          <rect
            x="0"
            y="0"
            width="48"
            height="48"
            class="influence-matrix__area__bg"
          />
          <text
            x="40"
            y="10"
            class="influence-matrix__area__text"
            text-anchor="end"
          >
            {{ $t("labels.critical") }}
          </text>
        </g>
        <g
          class="influence-matrix__area influence-matrix__area_passive"
          transform="translate(1, 1)"
        >
          <rect
            x="0"
            y="0"
            width="48"
            height="48"
            class="influence-matrix__area__bg"
          />
          <text x="6" y="10" class="influence-matrix__area__text">
            {{ $t("labels.passive") }}
          </text>
        </g>
        <g
          class="influence-matrix__area influence-matrix__area_buffer"
          transform="translate(1, 51)"
        >
          <rect
            x="0"
            y="0"
            width="48"
            height="48"
            class="influence-matrix__area__bg"
          />
          <text x="6" y="41" class="influence-matrix__area__text">
            {{ $t("labels.buffering") }}
          </text>
        </g>
        <g
          class="influence-matrix__area influence-matrix__area_active"
          transform="translate(51, 51)"
        >
          <rect
            x="0"
            y="0"
            width="48"
            height="48"
            class="influence-matrix__area__bg"
          />
          <text
            x="40"
            y="41"
            class="influence-matrix__area__text"
            text-anchor="end"
          >
            {{ $t("labels.active") }}
          </text>
        </g>

        <line class="influence-matrix__balanced-line" x1="0" y1="100" x2="100" y2="0"></line>

        <g>
          <cross-impact-matrix-point
            v-for="group in orderedPointGroups"
            :key="group.key"
            :x="group.x"
            :y="group.y"
            :points="group.points"
            @hover="onElementHover(group.key)"
          />
        </g>
      </svg>

      <div class="influence-matrix__legend-x">
        <template v-if="matrixType == 'impact'">
          {{ $t("labels.activeSum") }}
        </template>
        <template v-else>
          {{ $t("labels.outflow") }}
        </template>
      </div>
      <div class="influence-matrix__legend-y">
        <template v-if="matrixType == 'impact'">
          {{ $t("labels.passiveSum") }}
        </template>
        <template v-else>
          {{ $t("labels.inflow") }}
        </template>
      </div>
    </div>

    <!--<strong>Erklärungen zur Einflussmatrix</strong>
    <ul class="influence-matrix__explanation">
      <li class="influence-matrix__explanation__entry active">
        <strong>{{ $t("labels.active") }}{{ $t(":") }}</strong>
        Lorem ipsum...
      </li>
      <li class="influence-matrix__explanation__entry passive">
        <strong>{{ $t("labels.passive") }}{{ $t(":") }}</strong>
        Lorem ipsum...
      </li>
      <li class="influence-matrix__explanation__entry critical">
        <strong>{{ $t("labels.critical") }}{{ $t(":") }}</strong>
        Lorem ipsum...
      </li>
      <li class="influence-matrix__explanation__entry buffering">
        <strong>{{ $t("labels.buffering") }}{{ $t(":") }}</strong>
        Lorem ipsum...
      </li>
    </ul>-->

    <template v-if="ctx.impactMatrix?.relationType == 'impact'">
      <div class="label mt-4">
        {{ $t("labels.usedPriorityGridValue") }}
      </div>
      <radio-button
        :value="mode"
        :data="('maxInfluence' as const)"
        :label="$t('labels.relationStrength')"
        @update:value="mode = $event"
      />
      <radio-button
        :value="mode"
        :data="('slope' as const)"
        :label="$t('labels.currentRelationSlope')"
        @update:value="mode = $event"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
/**
 * Base: https://de.wikipedia.org/wiki/Papiercomputer_(Vester)
 */
import { computed, ref } from "vue";

import RadioButton from "@core/input/RadioButton.vue";
import CrossImpactMatrixPoint from "@editor/items/impactMatrix/CrossImpactMatrixPoint.vue";

import { useInspectorView } from "@editor/views/inspector/inspectorView.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useImpactMatrix } from "@editor/items/impactMatrix/useImpactMatrix";
import { useFlowBranchStore } from "@editor/items/flow/flowBranches.store";

const props = defineProps<{
  matrixId?: number;
}>();

const topCircle = ref<string>();

const inspectorView = useInspectorView();
const relationStore = useRelationStore();
const flowBranchStore = useFlowBranchStore();

const mode = computed({
  get() {
    return inspectorView.priorityMode;
  },
  set(mode: "maxInfluence" | "slope") {
    inspectorView.setPriorityMode(mode);
  }
});

const ctx = useImpactMatrix(() => props.matrixId ?? null);

const outgoingRelationsByUid = computed(
  () => relationStore.activeByStartUid
);
const incomingRelationsByUid = computed(
  () => relationStore.activeByEndUid
);
const outgoingFlowBranchesByElement = computed(
  () => flowBranchStore.outgoingByElementUid
);
const incomingFlowBranchesByElement = computed(
  () => flowBranchStore.incomingByElementUid
);

const activePriorities = computed(() => ctx.activePriorities);
const passivePriorities = computed(() => ctx.passivePriorities);

const matrixType = computed(() => ctx.impactMatrix?.relationType);

const points = computed(() => {
  // TODO: error: maxInfluence is zero, but it should have a value
  return Object.values(ctx.includedElements)
    // filter out elements without any relations
    .filter((element) => {
      if (ctx.impactMatrix?.relationType == "impact") {
        const relationsOut = (
          outgoingRelationsByUid.value[element.uid] ?? []
        ).filter((rel) => rel.maxInfluence != 0);
        const relationsIn = (
          incomingRelationsByUid.value[element.uid] ?? []
        ).filter((rel) => rel.maxInfluence != 0);
        return relationsIn.length + relationsOut.length > 0;
      }
      else {
        const branchesOut = outgoingFlowBranchesByElement.value[element.uid] ?? [];
        const branchesIn = incomingFlowBranchesByElement.value[element.uid] ?? [];
        return branchesIn.length + branchesOut.length > 0;
      }
    })
    .map((element) => {
      const active = activePriorities.value[element.uid];
      const passive = passivePriorities.value[element.uid];
      let className;
      if (active >= 0.5 && passive >= 0.5) className = "is-critical";
      else if (active >= 0.5 && passive < 0.5) className = "is-active";
      else if (active < 0.5 && passive >= 0.5) className = "is-passive";
      else if (active < 0.5 && passive < 0.5) className = "is-buffering";
      return {
        elementUid: element.uid,
        active,
        passive,
        className,
        positionKey: `${active} ${passive}`
      };
    });
});

const pointGroups = computed(() => {
  const groups: {
    [key: string]: {
      key: string;
      points: {
        elementUid: string;
        passive: number;
        active: number;
        positionKey: string;
        className?: string;
      }[];
      x: number;
      y: number;
    };
  } = {};
  for (const point of points.value) {
    if (!groups[point.positionKey]) {
      groups[point.positionKey] = {
        key: "",
        points: [],
        x: point.active,
        y: point.passive
      };
    }
    groups[point.positionKey].key += "_" + point.elementUid;
    groups[point.positionKey].points.push(point);
  }

  return Object.values(groups);
});

const orderedPointGroups = computed(() => {
  let groupArray = pointGroups.value;

  // handle Z-indexing (draw one group on top)
  if (topCircle.value) {
    const topEntryIndex = groupArray.findIndex(
      (group) => group.key == topCircle.value
    );
    const topEntry = groupArray[topEntryIndex];
    if (topEntry) {
      const groupArrayA = groupArray.slice(0, topEntryIndex);
      const groupArrayB = groupArray.slice(topEntryIndex + 1);
      groupArray = groupArrayA.concat(groupArrayB);
      groupArray.push(topEntry);
    }
  }

  return groupArray;
});

function onElementHover(pointGroupIndex: string) {
  topCircle.value = pointGroupIndex;
}
</script>

<style lang="scss">
.influence-matrix {
  width: 100%;
  height: 100%;
  font-size: 4px;
  grid-area: influence-matrix;

  &__area {
    &__bg {
      fill: var(--color-background-deep);
      stroke-width: 2px;
    }
    &_critical &__bg {
      stroke: var(--color-priority-critical);
    }
    &_buffer &__bg {
      stroke: var(--color-priority-buffering);
    }
    &_passive &__bg {
      stroke: var(--color-priority-passive);
    }
    &_active &__bg {
      stroke: var(--color-priority-active);
    }
    &__text {
      user-select: none;
      fill: var(--color-text);
    }

    .-flow > & {
      display: none;
    }
  }

  &__balanced-line {
    stroke: var(--color-grey);
    stroke-width: 0.5px;
    .-impact > & {
      display: none;
    }
  }

  &__explanation {
    padding-left: 0.5rem;
    &__entry {
      list-style: none;
      margin-bottom: 0.25rem;

      &::before {
        content: "";
        display: inline-block;
        height: 0.7em;
        width: 0.7em;
        border-radius: 2rem;
        margin: 0.25em;
        vertical-align: bottom;
      }
      &.active::before {
        background-color: var(--color-priority-active);
      }
      &.passive::before {
        background-color: var(--color-priority-passive);
      }
      &.critical::before {
        background-color: var(--color-priority-critical);
      }
      &.buffering::before {
        background-color: var(--color-priority-buffering);
      }
    }
  }

  &__grid {
    display: grid;
    --legend-width: 1.75rem;
    grid-template-columns: var(--legend-width) 1fr;
    grid-template-rows: 1fr var(--legend-width);
    grid-template-areas: "legend-y influence-matrix" ". legend-x";
  }

  &__legend-x,
  &__legend-y {
    font-family: var(--font-family-ui);
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }
  &__legend-y {
    grid-area: legend-y;
    white-space: nowrap;
    transform: rotate3d(0, 0, 1, -90deg);
  }
  &__legend-x {
    grid-area: legend-x;
  }
}
</style>
